import {
  type ArchiveDemoNoteTemplateRequest,
  type CreateDemoNoteTemplateRequest,
  type DemoNoteTemplate,
  type DemoNoteTemplateDefaults,
  DemoNoteTemplateDefaultsSchema,
  DemoNoteTemplateSchema,
  type DemoNoteTemplatesState,
  DemoNoteTemplatesStateSchema,
  type DuplicateDemoNoteTemplateRequest,
  type RestoreDemoNoteTemplateRequest,
  type UpdateDemoNoteTemplateDefaultsRequest,
  type UpdateDemoNoteTemplateRequest,
} from "@emr/contracts";
import { demoApiOrigin, demoFetch } from "./demoFetch";

async function noteTemplatesResponseError(response: Response, fallback: string): Promise<Error> {
  const body = (await response.json().catch(() => null)) as {
    readonly error?: { readonly message?: unknown };
  } | null;
  const message = body?.error?.message;
  return new Error(
    typeof message === "string" && message.length > 0
      ? message
      : `${fallback} (${response.status})`,
  );
}

async function requestJson<T>(
  path: string,
  parse: (value: unknown) => T,
  options: {
    readonly method?: "GET" | "POST";
    readonly body?: unknown;
    readonly signal?: AbortSignal | undefined;
  } = {},
): Promise<T> {
  const response = await demoFetch(`${demoApiOrigin()}${path}`, {
    method: options.method ?? "GET",
    ...(options.body === undefined
      ? {}
      : {
          headers: { "content-type": "application/json" },
          body: JSON.stringify(options.body),
        }),
    cache: "no-store",
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  if (!response.ok) {
    throw await noteTemplatesResponseError(response, "The synthetic note template request failed");
  }
  return parse(await response.json());
}

export interface DemoNoteTemplatesClient {
  getState(signal?: AbortSignal): Promise<DemoNoteTemplatesState>;
  createTemplate(
    input: CreateDemoNoteTemplateRequest,
    signal?: AbortSignal,
  ): Promise<DemoNoteTemplate>;
  updateTemplate(
    templateId: string,
    input: UpdateDemoNoteTemplateRequest,
    signal?: AbortSignal,
  ): Promise<DemoNoteTemplate>;
  duplicateTemplate(
    templateId: string,
    input: DuplicateDemoNoteTemplateRequest,
    signal?: AbortSignal,
  ): Promise<DemoNoteTemplate>;
  archiveTemplate(
    templateId: string,
    input: ArchiveDemoNoteTemplateRequest,
    signal?: AbortSignal,
  ): Promise<DemoNoteTemplate>;
  restoreTemplate(
    templateId: string,
    input: RestoreDemoNoteTemplateRequest,
    signal?: AbortSignal,
  ): Promise<DemoNoteTemplate>;
  updateDefaults(
    input: UpdateDemoNoteTemplateDefaultsRequest,
    signal?: AbortSignal,
  ): Promise<DemoNoteTemplateDefaults>;
}

export const demoNoteTemplatesClient: DemoNoteTemplatesClient = {
  getState(signal) {
    return requestJson(
      "/v1/demo/note-templates/state",
      (value) => DemoNoteTemplatesStateSchema.parse(value),
      { signal },
    );
  },

  createTemplate(input, signal) {
    return requestJson("/v1/demo/note-templates", (value) => DemoNoteTemplateSchema.parse(value), {
      method: "POST",
      body: input,
      signal,
    });
  },

  updateTemplate(templateId, input, signal) {
    return requestJson(
      `/v1/demo/note-templates/${encodeURIComponent(templateId)}/update`,
      (value) => DemoNoteTemplateSchema.parse(value),
      { method: "POST", body: input, signal },
    );
  },

  duplicateTemplate(templateId, input, signal) {
    return requestJson(
      `/v1/demo/note-templates/${encodeURIComponent(templateId)}/duplicate`,
      (value) => DemoNoteTemplateSchema.parse(value),
      { method: "POST", body: input, signal },
    );
  },

  archiveTemplate(templateId, input, signal) {
    return requestJson(
      `/v1/demo/note-templates/${encodeURIComponent(templateId)}/archive`,
      (value) => DemoNoteTemplateSchema.parse(value),
      { method: "POST", body: input, signal },
    );
  },

  restoreTemplate(templateId, input, signal) {
    return requestJson(
      `/v1/demo/note-templates/${encodeURIComponent(templateId)}/restore`,
      (value) => DemoNoteTemplateSchema.parse(value),
      { method: "POST", body: input, signal },
    );
  },

  updateDefaults(input, signal) {
    return requestJson(
      "/v1/demo/note-templates/defaults",
      (value) => DemoNoteTemplateDefaultsSchema.parse(value),
      { method: "POST", body: input, signal },
    );
  },
};
